import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import api from "@/utils/axios";
import cls from "./MemberProfileEditPayments.module.scss";

const MemberProfileEditPayments = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(); // common/en.json

  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError("");

        // Member-side endpoint (no userId in route)
        const res = await api.get("/v1/member/profile/payment-submissions");

        if (!isMounted) return;

        setSubmissions(res.data?.submissions || []);
      } catch (err) {
        if (!isMounted) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          t("memberProfilePaymentsList.errors.genericLoadError");
        setError(msg);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSubmissions();

    return () => {
      isMounted = false;
    };
  }, [t]);

  const stats = useMemo(() => {
    const total = submissions.length;
    const pending = submissions.filter((s) => s.status === "pending").length;
    const approved = submissions.filter((s) => s.status === "approved").length;
    const decline = submissions.filter((s) => s.status === "decline").length;
    return { total, pending, approved, decline };
  }, [submissions]);

  const handleBack = () => {
    // Back to member profile edit overview
    navigate("/lounge/my-profile/edit");
  };

  const handleViewDetails = (submissionId) => {
    // Single view route: my-profile/edit/payments/:memberPaymentSubmissionId
    navigate(`/lounge/my-profile/edit/payments/${submissionId}`);
  };

  const formatDate = (value) => {
    if (!value) return t("memberProfilePaymentsList.common.noValue");
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return t("memberProfilePaymentsList.common.noValue");
    }
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const formatDateTime = (value) => {
    if (!value) return t("memberProfilePaymentsList.common.noValue");
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return t("memberProfilePaymentsList.common.noValue");
    }
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderStatusLabel = (status) => {
    if (status === "approved") {
      return t("memberProfilePaymentsList.status.approved");
    }
    if (status === "decline" || status === "rejected") {
      return t("memberProfilePaymentsList.status.declined");
    }
    return t("memberProfilePaymentsList.status.pending");
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          <span className={cls.backIcon} aria-hidden="true">
            ←
          </span>
          <span>{t("memberProfilePaymentsList.actions.backToProfile")}</span>
        </button>

        <div className={cls.headerText}>
          <h1 className={cls.title}>{t("memberProfilePaymentsList.title")}</h1>
          <p className={cls.subtitle}>
            {t("memberProfilePaymentsList.subtitle")}
          </p>
        </div>
      </header>

      <section className={cls.statsRow}>
        <div className={cls.statCard}>
          <span className={cls.statLabel}>
            {t("memberProfilePaymentsList.stats.total")}
          </span>
          <span className={cls.statValue}>{stats.total}</span>
        </div>
        <div className={cls.statCard}>
          <span className={cls.statLabel}>
            {t("memberProfilePaymentsList.stats.pending")}
          </span>
          <span className={cls.statValuePending}>{stats.pending}</span>
        </div>
        <div className={cls.statCard}>
          <span className={cls.statLabel}>
            {t("memberProfilePaymentsList.stats.approved")}
          </span>
          <span className={cls.statValueApproved}>{stats.approved}</span>
        </div>
        <div className={cls.statCard}>
          <span className={cls.statLabel}>
            {t("memberProfilePaymentsList.stats.declined")}
          </span>
          <span className={cls.statValueRejected}>{stats.decline}</span>
        </div>
      </section>

      {loading && (
        <div className={cls.stateBox}>
          <div className={cls.loader} />
          <span>{t("memberProfilePaymentsList.states.loading")}</span>
        </div>
      )}

      {!loading && error && (
        <div className={cls.stateBoxError}>
          <span className={cls.stateLabel}>
            {t("memberProfilePaymentsList.states.errorLabel")}
          </span>
          <p className={cls.stateMessage}>{error}</p>
        </div>
      )}

      {!loading && !error && submissions.length === 0 && (
        <div className={cls.stateBoxEmpty}>
          <h2 className={cls.emptyTitle}>
            {t("memberProfilePaymentsList.states.emptyTitle")}
          </h2>
          <p className={cls.emptyText}>
            {t("memberProfilePaymentsList.states.emptyText")}
          </p>
        </div>
      )}

      {!loading && !error && submissions.length > 0 && (
        <Motion.ul
          className={cls.list}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {submissions.map((s, index) => {
            const methodTitle =
              s.paymentOption?.title ||
              t("memberProfilePaymentsList.list.unknownPaymentType");
            const status = s.status || "pending";

            return (
              <Motion.li
                key={s.id}
                className={cls.card}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * index, duration: 0.25 }}
              >
                <div className={cls.cardMain}>
                  <div className={cls.cardTopRow}>
                    <div className={cls.cardTitleGroup}>
                      <h2 className={cls.cardTitle}>{methodTitle}</h2>
                      <p className={cls.cardSubtitle}>
                        {t("memberProfilePaymentsList.list.submissionIdLabel")}{" "}
                        <span className={cls.cardId}>{s.id}</span>
                      </p>
                    </div>

                    <span
                      className={`${cls.statusBadge} ${
                        cls[`status-${status}`] || ""
                      }`}
                    >
                      {renderStatusLabel(status)}
                    </span>
                  </div>

                  <div className={cls.metaRow}>
                    <div className={cls.metaItem}>
                      <span className={cls.metaLabel}>
                        {t("memberProfilePaymentsList.list.transferDateLabel")}
                      </span>
                      <span className={cls.metaValue}>
                        {formatDate(s.transferDate)}
                      </span>
                    </div>
                    <div className={cls.metaItem}>
                      <span className={cls.metaLabel}>
                        {t("memberProfilePaymentsList.list.submittedOnLabel")}
                      </span>
                      <span className={cls.metaValue}>
                        {formatDateTime(s.createdAt)}
                      </span>
                    </div>
                    <div className={cls.metaItem}>
                      <span className={cls.metaLabel}>
                        {t("memberProfilePaymentsList.list.lastUpdatedLabel")}
                      </span>
                      <span className={cls.metaValue}>
                        {formatDateTime(s.updatedAt)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={cls.cardActions}>
                  <button
                    type="button"
                    className={cls.viewBtn}
                    onClick={() => handleViewDetails(s.id)}
                  >
                    <span>
                      {t("memberProfilePaymentsList.actions.viewDetails")}
                    </span>
                    <span className={cls.viewIcon} aria-hidden="true">
                      →
                    </span>
                  </button>
                </div>
              </Motion.li>
            );
          })}
        </Motion.ul>
      )}
    </div>
  );
};

export default MemberProfileEditPayments;
